import noop from 'lodash/noop';
import {
  Directive,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnChanges,
  OnDestroy,
  NgZone,
  Inject,
} from '@angular/core';
import { MapComponent } from './map.component';
import { ICONS, IIconInfo } from './marker-icons';

import { EntityState } from '../lib/entity.const';
import { GREEN_40 } from '@lib/colors';

const UNSELECTED_MARKER_OPACITY = 0.5;

export interface IInfoWindow {
  content: string;
  verticalOffset: number;
}

export interface IPixelOffset {
  pixelOffsetX: number;
  pixelOffsetY: number;
}

@Directive({
  selector: 'map-marker',
})
export class MapMarkerDirective implements OnInit, OnChanges, OnDestroy {
  @Input() public lat: number;
  @Input() public lng: number;
  @Input() public title: string;
  @Input() public type: string;
  @Input() public zIndex: number;
  @Input() public visible: boolean;
  @Input() public autoZoom: boolean;
  @Input() public bearing: number;
  @Input() public label: google.maps.MarkerLabel;
  @Input() public info: IInfoWindow;
  @Output() public onClick = new EventEmitter();
  @Output() public onDblClick = new EventEmitter();
  public markerObj: google.maps.Marker;
  private gmaps;
  private id;
  private imageName: string;
  private createMarkerPromise = new Promise((resolve, reject) => reject());

  constructor(@Inject(MapComponent) private map: MapComponent, private ngZone: NgZone) {}

  public ngOnInit() {
    this.createMarkerPromise = this.map.createMapPromise
      .then(() => {
        this.gmaps = this.map.gmaps;
        return this.create();
      });
    this.map.markerPromiseList.push(this.createMarkerPromise);
  }

  public ngOnChanges(changes) {
    const performUpdates = () => {
      if (this.id) {
        if (changes.zIndex) this.markerObj.setZIndex(this.zIndex);
        if (changes.type) this.update(changes.type);
        if (changes.visible) this.markerObj.setVisible(this.visible);
      }
    };
    this.createMarkerPromise.then(performUpdates, noop);
  }

  public ngOnDestroy() {
    this.remove();
  }

  private getIcon(type: string = this.type): IIconInfo { return ICONS[type]; }

  private getPosition() {
    return new google.maps.LatLng(this.lat, this.lng);
  }

  private create() {
    const config: google.maps.MarkerOptions = {
      label: this.label,
      position: this.getPosition(),
      title: this.title,
      zIndex: this.zIndex || undefined,
      visible: this.visible === undefined ? true : this.visible,
    };

    this.markerObj = new google.maps.Marker(config);
    const promise: Promise<any> = this.getMarker();
    return promise.then(() => {
      this.ngZone.runOutsideAngular(() => this.attachEvents());
      const options = {
        autoZoom: typeof this.autoZoom === 'boolean' ? this.autoZoom : true,
      };
      this.id = this.map.addMarker(this.markerObj, options);
    });
  }

  private update(changes) {
    const markerObj = this.markerObj;
    markerObj.setPosition(this.getPosition());
    const promise: Promise<any> = this.getMarker();
    return promise.then(() => {
      if (this.zIndex != null) markerObj.setZIndex(this.zIndex);
      if (this.visible != null) markerObj.setVisible(this.visible);
      const checkHead = type =>
        type.includes('head') ||
        type === 'asset-motion-selected' ||
        type === 'asset-stationary-selected' ||
        type === EntityState.IDLING ||
        type === EntityState.MOVING ||
        type === EntityState.OFF
      ;
      if (checkHead(this.type)) markerObj.setVisible(true);
      else if (checkHead(changes.previousValue)) markerObj.setVisible(this.visible);
    });
  }

  private drawArrowDriving() {
    this.imageName = 'arrow-driving';
    return this.generateImage(UNSELECTED_MARKER_OPACITY).then(iconDrawingInfoParam => {
      const iconDrawingInfo: any = iconDrawingInfoParam;
      this.markerObj.setIcon({
        url: iconDrawingInfo.image,
        anchor: new google.maps.Point(iconDrawingInfo.anchor.x, iconDrawingInfo.anchor.y),
        scaledSize: new google.maps.Size(32, 32),
      });
      this.markerObj.setShape(iconDrawingInfo.shape);
    }).catch(noop);
  }

  private drawArrowHead() {
    this.imageName = 'arrow-head';
    return this.generateImage().then(iconDrawingInfoParam => {
      const iconDrawingInfo: any = iconDrawingInfoParam;
      this.markerObj.setIcon({
        url: iconDrawingInfo.image,
        anchor: new google.maps.Point(iconDrawingInfo.anchor.x, iconDrawingInfo.anchor.y),
        scaledSize: new google.maps.Size(40, 40),
      });
      this.markerObj.setShape(iconDrawingInfo.shape);
    }).catch(noop);
  }

  private drawCircleDriving() {
    const circleDriving = 'circle-driving';
    return new Promise(resolve => {
      const { url, anchor } = this.getIcon(circleDriving);
      this.markerObj.setOptions({
        icon: {
          url,
          anchor: new google.maps.Point(anchor.x, anchor.y),
        },
        position: this.getPosition(),
      });
      this.markerObj.setOpacity(UNSELECTED_MARKER_OPACITY);
      resolve(true);
    });
  }

  private drawDispatchCircle(): void {
    const map: google.maps.Map = this.map.map;
    const radius = 1609; // 1 mile = 1609 metre
    const circle: google.maps.Circle = new google.maps.Circle({
      map,
      radius,
      fillColor: GREEN_40,
      strokeWeight: 0,
    });
    circle.bindTo('center', this.markerObj, 'position');
  }

  private drawCircleHead() {
    return this.setMarkerIcon('circle-head');
  }

  private drawIdleState() {
    return this.setMarkerIcon('vehicle-idle');
  }

  private drawOffState() {
    return this.setMarkerIcon('vehicle-off');
  }

  private attachEvents() {
    const markerObj = this.markerObj;
    this.gmaps.event.addListener(markerObj, 'click', () => this.onClick.emit());
    this.gmaps.event.addListener(markerObj, 'dblclick', () => this.onDblClick.emit());
  }

  private remove() {
    if (!this.id) return;
    this.gmaps.event.clearInstanceListeners(this.markerObj);
    this.map.removeMarker(this.id);
  }

  private getMarker(): Promise<void> {
    let promise: any;
    if (this.type === EntityState.IDLING) {
      return this.drawIdleState();
    } else if (this.type === EntityState.OFF) {
      return this.drawOffState();
    }

    if (this.type === 'asset-motion-selected') return this.drawAssetMotionSelected();
    else if (this.type === 'asset-motion') return this.drawAssetMotion();
    else if (this.type === 'asset-stationary-selected') return this.drawAssetStationarySelected();
    else if (this.type === 'asset-stationary') return this.drawAssetStationary();
    else if (this.type === 'dispatch-unknown') this.drawDispatchCircle();
    if (this.bearing !== undefined) {
      if (this.type.includes('head')) promise = this.drawArrowHead();
      else if (this.type.includes('driving')) promise = this.drawArrowDriving();
    } else {
      if (this.type.includes('head')) promise = this.drawCircleHead();
      else if (this.type.includes('driving')) promise = this.drawCircleDriving();
      else {
        promise = Promise.resolve().then(() => {
          const icon = this.getIcon();
          return this.markerObj.setIcon(icon as any);
        });
      }
    }
    return promise;
  }

  private drawAssetMotionSelected(): Promise<void> {
    return this.drawArrowHead();
  }

  private drawAssetMotion(): Promise<void> {
    this.imageName = 'asset-motion';
    return this.generateImage().then(iconDrawingInfoParam => {
      const iconDrawingInfo: any = iconDrawingInfoParam;
      this.markerObj.setIcon({
        url: iconDrawingInfo.image,
        anchor: new google.maps.Point(iconDrawingInfo.anchor.x, iconDrawingInfo.anchor.y),
        scaledSize: new google.maps.Size(30, 30),
      });
      this.markerObj.setShape(iconDrawingInfo.shape);
    }).catch(noop);
  }

  private drawAssetStationarySelected(): Promise<void> {
    return this.setMarkerIcon('asset-stationary-selected');
  }

  private drawAssetStationary(): Promise<void> {
    return this.setMarkerIcon('asset-stationary');
  }

  private generateImage(opacity: number = 1) {
    const imageName = this.imageName;
    const { url, anchor, shape, shouldRotate } = this.getIcon(imageName);
    const degrees = this.bearing != null ? this.bearing : 0;
    const img = new Image();
    img.src = url;
    const type = this.type;
    return new Promise((resolve, reject) => {
      this.ngZone.runOutsideAngular(() => {
        (img as any).addEventListener('load', () => {
          if (this.type !== type) return reject();
          let canvas: HTMLCanvasElement = null;
          if (imageName === 'arrow-driving') canvas = this.createCanvas(64, 64);
          else if (imageName === 'asset-motion') canvas = this.createCanvas(88, 88);
          else if (imageName === 'arrow-head') canvas = this.createCanvas(92, 92);
          else canvas = this.createCanvas(128, 128);

          const context = canvas.getContext('2d');
          context.save();
          context.globalAlpha = opacity;
          if (imageName === 'arrow-driving') this.rotateAndPaint(context, img, 32, 32, 16, 16, degrees);
          else if (imageName === 'asset-motion') this.rotateAndPaint(context, img, 44, 44, 62, 62, degrees);
          else if (imageName === 'arrow-head') this.rotateAndPaint(context, img, 46, 46, 64, 64, degrees);
          const base64Image = canvas.toDataURL('image/png');
          context.restore();
          resolve({ image: base64Image, anchor, shape });
        }, { once: true });
      });
    });
  }

  private createCanvas(width: number, height: number): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    return canvas;
  }

  private rotateAndPaint(context, image, x, y, width, height, rotation = 0) {
    context.save();
    context.translate(x - (width / 2), y - (height / 2));
    context.translate(width / 2, height / 2);
    context.rotate(rotation * (Math.PI / 180));
    context.drawImage(image, -width / 2, -height / 2, width, height);
    context.restore();
  }

  private setMarkerIcon(type: string, opacity = 1): Promise<void> {
    return new Promise(resolve => {
      const icon = this.getIcon(type);
      this.markerObj.setIcon(icon as any);
      this.markerObj.setOpacity(opacity);
      resolve();
    });
  }
}
